import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Link2, Save } from "lucide-react";

interface SiteLink {
  id: string;
  link_key: string;
  label: string;
  url: string;
}

interface Props {
  sessionToken: string;
}

const LinkManagement = ({ sessionToken }: Props) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [links, setLinks] = useState<SiteLink[]>([]);
  const { toast } = useToast();

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "list_site_links", sessionToken },
      });
      if (res.data?.links) setLinks(res.data.links);
    } catch {
      toast({ title: "Failed to load links", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLinks(); }, []);

  const handleUrlChange = (id: string, newUrl: string) => {
    setLinks(prev => prev.map(l => l.id === id ? { ...l, url: newUrl } : l));
  };

  const handleSave = async (link: SiteLink) => {
    setSaving(link.id);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "update_site_link", sessionToken, linkId: link.id, url: link.url, label: link.label },
      });
      if (res.data?.error) throw new Error(res.data.error);
      toast({ title: `${link.label} updated!` });
    } catch (err) {
      const error = err as Error;
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link2 className="w-5 h-5" />
          Site Link Management
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {links.map((link) => (
          <div key={link.id} className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
            <div className="flex-1 space-y-1 w-full">
              <Label className="text-sm font-medium">{link.label}</Label>
              <Input
                value={link.url}
                onChange={(e) => handleUrlChange(link.id, e.target.value)}
                placeholder="https://..."
                className="text-sm"
              />
            </div>
            <Button
              size="sm"
              onClick={() => handleSave(link)}
              disabled={saving === link.id}
              className="shrink-0"
            >
              {saving === link.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default LinkManagement;
